/**
 * Parser for the ประกันสังคม report PDFs (downloaded per branch from mms.sso.go.th/portal/).
 *
 * ⚠️ THREE LAYOUTS ARE IN USE AT ONCE, AND NOTHING HERE ASKS WHICH ONE IT HAS.
 *   เดิม  "รายงานการส่งเบิกของสถานพยาบาล"  dates 01/07/2026 (AD), money 900.00, footer รวม
 *         ID ชื่อ ใช้สิทธิ ส่งเบิก ประเภท สถานะ ขอเบิก มีสิทธิ ใบสำคัญ โอน
 *   ใหม่  REPORT_02 "รายงานการจ่ายค่าบริการ…"  dates 01 กันยายน 2569 (BE), money 900, รวมทั้งสิ้น
 *         ID ชื่อ ใช้สิทธิ ส่งเบิก ประเภท ขอเบิก จ่าย ใบสำคัญ โอน          (no สถานะ)
 *   ใหม่  REPORT_01 "รายงานการส่งเบิกค่าบริการ…"  same dates + money + footer as REPORT_02
 *         ID ชื่อ ใช้สิทธิ ส่งเบิก ประเภท สถานะ ขอเบิก                     (no ใบสำคัญ, no โอน)
 * The row shape coming out is the same for all three, so reconcile and the page need no
 * branch. The anchors below each accept both spellings instead. See
 * docs/reimbursement-sso-pdf-import.md §8.
 *
 * The file has a real text layer — no OCR, no image work. Extraction is split in two so
 * the fragile half is isolated: `extractLines()` owns pdf.js and turns a file into lines
 * of text; `parseSsoReport()` is pure, takes those lines, and is the part worth trusting.
 *
 * ⚠️ THE ROWS ARE PARSED BY ANCHOR, NOT BY COLUMN POSITION. pdf.js hands back text runs
 * whose spacing depends on the font metrics, so the multi-space gaps that make the table
 * readable in `pdftotext -layout` are not reliably there. Instead each row is located by
 * the things that cannot be mistaken for anything else — a 13-digit national ID, three
 * dd/mm/yyyy dates, two 2-decimal amounts — and the free-text fields are read from the
 * gaps between them. That survives a column being widened or re-ordered slightly.
 *
 * ⚠️ THE HEADER IS BUDDHIST ERA, THE OLD ROWS ARE NOT. "ตั้งแต่วันที่ 01 กรกฎาคม 2569" is BE
 * while the old row dates read "01/07/2026" in AD. Both appear in the same file. Converting
 * the wrong one silently shifts the window by 543 years and returns an empty diff. The 2026
 * layouts write the rows like the header, "01 กันยายน 2569", which is BE. The spelling says
 * which calendar it is: never convert a slash date, always convert a month name.
 *
 * ⚠️ รหัสสถานพยาบาล IS NOT ALWAYS DIGITS. The older branches carry a 5-digit code
 * (สนามเป้า/อารีย์/Tooth Taxi all share 54104), but the 2025 openings carry a LETTER and
 * four digits — แจ้งวัฒนะ E0388, บางกะปิ E0387, รังสิต E0442 — even though the label still
 * says "(5 หลัก)". A digits-only match returns "" for those, and an empty code is not a
 * soft failure: the page refuses to reconcile without it. Both shapes are accepted, and
 * the result is upper-cased because `Branch.ssoclinic` is compared for exact equality
 * in Xano (`GET /sso/claims`, api 625).
 *
 * ⚠️ A TABLE CELL CAN WRAP OUT OF ITS OWN ROW. pdf.js emits one text run per cell at that
 * cell's baseline, so a cell that wraps onto two lines lands in two Y buckets, NEITHER of
 * which is the row's. In the August แจ้งวัฒนะ file every สถานะรับแจ้ง reads
 * "รอจ่าย/ส่งเลขคำสั่ง" / "จ่ายให้ Core" on two lines and so left the row entirely.
 * `linesFromItems()` folds those fragments back into the row they belong to and keeps the
 * cell boundaries as tabs, because the alternative — splitting the joined text on spaces —
 * ate the last word of ประเภทค่าบริการ ("อุด ขูด ถอน" parsed as type "อุด ขูด", status
 * "ถอน"). The same fold is what stops a wrapped ชื่อ-สกุล from silently truncating, and a
 * truncated name IS load-bearing: ~9% of rows match on name alone.
 */

const TH_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

const pad = (n) => String(n).padStart(2, '0')

/** The cell separator `linesFromItems` writes and `parseSsoReport` reads. A tab never
 *  occurs in the PDF's own text layer, so its presence in a line always means "these are
 *  real column boundaries, not guesses". */
const CELL = '\t'

/** A date cell in a row, in either layout: "01/07/2026" or "01 กันยายน 2569". */
const ROW_DATE = new RegExp(`\\d{2}/\\d{2}/\\d{4}|\\d{1,2}\\s*(?:${TH_MONTHS.join('|')})\\s*\\d{4}`, 'g')

/** "01/07/2026" (already AD) or "01 กันยายน 2569" (BE) → "2026-07-01". */
function rowDateToISO(s) {
  const text = String(s).trim()
  const ad = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text)
  if (ad) return `${ad[3]}-${ad[2]}-${ad[1]}`
  const be = /^(\d{1,2})\s*([ก-๙]+)\s*(\d{4})$/.exec(text)
  return be ? headerDateToISO(be[1], be[2], be[3]) : ''
}

/** "01 กรกฎาคม 2569" → "2026-07-01". Header dates are BE. */
function headerDateToISO(d, monthName, be) {
  const mi = TH_MONTHS.indexOf(String(monthName).trim())
  if (mi < 0) return ''
  const year = Number(be) - 543
  if (!Number.isFinite(year) || year < 1900 || year > 2200) return ''
  return `${year}-${pad(mi + 1)}-${pad(Number(d))}`
}

const money = (s) => Number(String(s).replace(/,/g, ''))

/**
 * The money cells of a row and where they start, with the voucher and transfer date already
 * cut off `body`. "900.00" cannot be mistaken for anything. A bare "900" can — a service type
 * may hold a number of its own ("ฟันปลอม 5 ซี่") — so bare figures are read only as the
 * unbroken run at the END of the body: ขอเบิก, then จ่าย when the report has that column.
 */
function readAmounts(body) {
  const decimals = body.match(/[\d,]+\.\d{2}/g)
  if (decimals) return { amounts: decimals, at: body.indexOf(decimals[0]) }
  const tail = /(^|\s)(\d[\d,]*(?:\s+\d[\d,]*)?)\s*$/.exec(body)
  if (!tail) return { amounts: [], at: -1 }
  return { amounts: tail[2].split(/\s+/), at: tail.index + tail[1].length }
}

/* ── the pure parser ───────────────────────────────────────────────────────── */

/**
 * @param {string[]} lines  text lines, in reading order, across all pages
 * @returns {{header, rows, footer, warnings}}
 */
export function parseSsoReport(lines) {
  const warnings = []
  const all = lines.map((l) => String(l ?? '').replace(/ /g, ' ').trim()).filter(Boolean)
  const blob = all.join('\n')

  /* header ------------------------------------------------------------------ */
  // Anchor on the COLON, not on "first digits after the label": the label itself reads
  // "รหัสสถานพยาบาล (5 หลัก) :" and that 5 is a digit that will happily be captured.
  // The optional leading letters are the E-prefixed codes the 2025 branches were issued.
  const ssoclinic = ((/รหัสสถานพยาบาล[^:\n]*:\s*([A-Za-z]{0,2}\d{3,6})/.exec(blob) || [])[1] || '')
    .toUpperCase()
  const clinicName = (/สถานพยาบาล\s*:\s*([^\n]+?)\s*$/m.exec(blob) || [])[1] || ''

  // ⚠️ สถานที่รับบริการ IS WHAT SPLITS A SHARED CODE. 54104 is held by สนามเป้า, อารีย์ AND
  // Tooth Taxi, and ปกส. issues the mobile unit its own report under the same code and the
  // same "สาขาอารีย์" name — the ONLY difference is this line: "สถานพยาบาล" vs "รถทันตกรรม".
  // Without it a Tooth Taxi file is diffed against the whole 54104 set, and อารีย์'s own
  // claims pour into its "ยังไม่ได้รับเงิน" bucket. 'mobile' | 'clinic' | '' (line absent).
  const service_site_raw = ((/สถานที่รับบริการ\s*:?\s*([^\n\t]+?)\s*$/m.exec(blob) || [])[1] || '').trim()
  const service_site = /รถ/.test(service_site_raw) ? 'mobile'
    : /สถานพยาบาล|คลินิก/.test(service_site_raw) ? 'clinic' : ''

  let start_date = ''
  let end_date = ''
  const win = /ตั้งแต่วันที่\s*(\d{1,2})\s*([ก-๙]+)\s*(\d{4})\s*ถึงวันที่\s*(\d{1,2})\s*([ก-๙]+)\s*(\d{4})/.exec(blob)
  if (win) {
    start_date = headerDateToISO(win[1], win[2], win[3])
    end_date = headerDateToISO(win[4], win[5], win[6])
  }

  /* rows -------------------------------------------------------------------- */
  const rows = []
  for (const line of all) {
    // Anchor 1: a running number followed by a 13-digit citizen ID starts a data row.
    const head = /^(\d{1,4})\s+(\d{13})\s+(.*)$/.exec(line)
    if (!head) continue
    const [, seq, idcard, rest] = head

    // Anchor 2: the dates — ใช้สิทธิ, ส่งเบิก, then โอนเข้าบัญชี when the report has it.
    const dates = rest.match(ROW_DATE) || []
    if (dates.length < 2) {
      warnings.push(`บรรทัดที่ ${seq}: อ่านวันที่/จำนวนเงินไม่ครบ`)
      continue
    }

    // ⚠️ วันที่ใช้สิทธิ and วันที่ส่งเบิก are usually the SAME string, so each date must
    // be located AFTER the previous one ends. A plain indexOf(dates[1]) re-finds date 0
    // and drags a date into ประเภทค่าบริการ.
    const p0 = rest.indexOf(dates[0])
    const p1 = rest.indexOf(dates[1], p0 + dates[0].length)

    // ชื่อ-สกุล is everything between the ID and the first date. The PDF glues the
    // title to the given name with no space ("นางสาวศุทธิณี"); stripping it is the
    // server's job so both sides of the match normalise identically.
    const raw_name = rest.slice(0, p0).trim()

    // เลขที่ใบสำคัญรับเงิน — a long digit run with a letter suffix (…MD).
    const after = rest.slice(p1 + dates[1].length)
    const voucher = (/\b(\d{8,}[A-Z]{1,3})\b/.exec(after) || [])[1] || ''

    // Anchor 3: the money — ขอเบิก, then มีสิทธิ / จ่าย when the report has it.
    const ends = [voucher, dates[2]].filter(Boolean).map((v) => after.indexOf(v))
    const body = after.slice(0, Math.min(after.length, ...ends))
    const { amounts, at } = readAmounts(body)
    if (!amounts.length) {
      warnings.push(`บรรทัดที่ ${seq}: อ่านวันที่/จำนวนเงินไม่ครบ`)
      continue
    }

    // ประเภทค่าบริการ + สถานะรับแจ้ง sit between the 2nd date and the 1st amount, and they
    // are two SEPARATE cells — `linesFromItems` keeps that boundary as a tab. Splitting on
    // whitespace instead is wrong in principle and wrong in practice: the service type
    // contains spaces of its own ("อุด ขูด ถอน"), so taking the last token as the status
    // ate the "ถอน" the moment a report shipped its status in a wrapped cell.
    const mid = at > 0 ? body.slice(0, at) : ''
    let service_type
    let status
    if (line.includes(CELL)) {
      const cells = mid.split(CELL).map((c) => c.trim()).filter(Boolean)
      service_type = cells[0] ?? ''
      status = cells.slice(1).join(' ')
    } else {
      // No cell information at all — a hand-written fixture, or text from some other
      // extractor. Fall back to the old last-token guess, which is right only when the
      // status happens to be a single word.
      const midParts = mid.trim().split(/\s+/).filter(Boolean)
      status = midParts.length ? midParts[midParts.length - 1] : ''
      service_type = midParts.slice(0, -1).join(' ')
    }

    rows.push({
      seq: Number(seq),
      idcard,
      raw_name,
      service_date: rowDateToISO(dates[0]),
      submit_date: rowDateToISO(dates[1]),
      service_type,
      status,
      amount: money(amounts[0]),
      entitled: amounts[1] != null ? money(amounts[1]) : money(amounts[0]),
      voucher,
      transfer_date: dates[2] ? rowDateToISO(dates[2]) : '',
    })
  }

  /* footer ------------------------------------------------------------------ */
  // The report's own รวม. This is the integrity check that makes a truncated parse
  // loud instead of silent — a half-read file that still looks complete is the one
  // failure mode here that would actually cost money.
  let footer_total = null
  // NB: no \b here — JS word boundaries are ASCII-centric, so /^รวม\b/ never matches a
  // Thai label followed by a space and silently skips the integrity check.
  const totalLine = all.find((l) => /^รวม(?:ทั้งสิ้น)?[\s:]/.test(l) || /รวม\s+[\d,]+\.\d{2}/.test(l))
  if (totalLine) {
    const amts = totalLine.match(/[\d,]+\.\d{2}/g) || totalLine.match(/\d[\d,]*/g) || []
    if (amts.length) footer_total = money(amts[0])
  }

  const parsed_total = rows.reduce((t, r) => t + r.amount, 0)
  const reconciles = footer_total != null && Math.abs(footer_total - parsed_total) < 0.005

  if (!ssoclinic) warnings.push('ไม่พบรหัสสถานพยาบาลในหัวรายงาน')
  if (!start_date || !end_date) warnings.push('ไม่พบช่วงวันที่ในหัวรายงาน')
  if (!rows.length) warnings.push('ไม่พบรายการในไฟล์นี้')
  if (footer_total == null) warnings.push('ไม่พบยอดรวมท้ายรายงาน — ข้ามการตรวจสอบยอด')

  // Duplicate national IDs inside one report are not expected (137 unique in the July
  // file). If they appear, say so — it changes what a 1-to-1 match even means.
  const seen = new Set()
  const dupes = new Set()
  for (const r of rows) { if (seen.has(r.idcard)) dupes.add(r.idcard); seen.add(r.idcard) }
  if (dupes.size) warnings.push(`มีเลขบัตรซ้ำ ${dupes.size} เลข — ตรวจสอบก่อนยืนยัน`)

  // ⚠️ The commit closes a line as รับแล้ว at the claim amount and never reads the paid
  // figure. A short payment would close unseen, so at least say so before the confirm.
  const short = rows.filter((r) => r.entitled !== r.amount)
  if (short.length) warnings.push(`มี ${short.length} รายการที่ยอดจ่าย/ยอดมีสิทธิไม่เท่ากับยอดขอเบิก — ตรวจสอบก่อนยืนยัน`)

  // REPORT_01 has no payment columns at all, so a row there with สถานะ "จ่ายแล้ว" and no
  // voucher is not unpaid, the report just cannot prove it. Its column label is what tells
  // that apart from an old-layout file whose rows simply have not been paid yet.
  const has_voucher_column = /ใบสำคัญ/.test(blob) || rows.some((r) => r.voucher)

  return {
    header: { ssoclinic, clinicName, service_site, service_site_raw, start_date, end_date, has_voucher_column },
    rows,
    footer: { footer_total, parsed_total, reconciles },
    warnings,
  }
}

/* ── layout → lines (pure) ─────────────────────────────────────────────────── */

/** A data row always opens with the running number and the 13-digit national ID. */
const ROW_START = /^\d{1,4}\s+\d{13}(\s|$)/
/** A date or a 2-decimal amount means a real table line (the รวม footer), not a wrap. The
 *  2026 footer "รวมทั้งสิ้น 57600" has no decimals to catch, so it is named outright. */
const HAS_FIGURE = /\d{2}\/\d{2}\/\d{4}|[\d,]+\.\d{2}|^รวม/
/** Point gap that separates two columns. The narrowest real gap in this report is the
 *  13pt between เลขประจำตัวประชาชน and ชื่อ-สกุล; runs inside one cell are contiguous. */
const CELL_GAP = 6

function median(xs) {
  const s = xs.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b)
  if (!s.length) return 0
  const m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/**
 * One Y bucket → one line, with tabs at the real column boundaries.
 * Runs are grouped into columns by their horizontal extent, so two fragments of the SAME
 * wrapped cell (which overlap in X) stay together and are read top line first.
 */
function cellsToText(cells) {
  const cols = []
  for (const c of [...cells].sort((a, b) => a.x - b.x || b.y - a.y)) {
    const last = cols[cols.length - 1]
    if (last && c.x - last.end <= CELL_GAP) {
      last.parts.push(c)
      last.end = Math.max(last.end, c.x + c.w)
    } else {
      cols.push({ end: c.x + c.w, parts: [c] })
    }
  }
  return cols
    .map((col) => col.parts
      .sort((a, b) => b.y - a.y)
      .map((p) => p.str.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join(' '))
    .filter(Boolean)
    .join(CELL)
}

/**
 * Text runs of ONE page → lines, in reading order.
 * @param {{str: string, x: number, y: number, w: number}[]} items
 *
 * ⚠️ The fold at the end is the whole point. A cell that wraps sits at its own baseline,
 * not the row's, so without this its text is emitted as a separate orphan line and the
 * row loses that column entirely. Fragments are folded into the nearest data row when
 * they sit well inside the row pitch and carry no date or amount of their own — that
 * last guard is what keeps the รวม footer, which sits just under the final row, out.
 * A page holding fewer than two data rows has no measurable pitch and folds nothing;
 * the cost is a blank สถานะรับแจ้ง on such a page, never a wrong figure.
 */
export function linesFromItems(items) {
  const buckets = new Map()
  for (const it of items ?? []) {
    const str = String(it?.str ?? '')
    if (!str.trim()) continue
    const y = Number(it.y) || 0
    // Rounding absorbs sub-pixel baseline drift within a row without merging two rows.
    const key = Math.round(y * 2) / 2
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push({ x: Number(it.x) || 0, w: Number(it.w) || 0, y, str })
  }

  const ys = [...buckets.keys()].sort((a, b) => b - a)          // top of the page down
  const text = new Map(ys.map((y) => [y, cellsToText(buckets.get(y))]))

  const rowYs = ys.filter((y) => ROW_START.test(text.get(y)))
  const pitch = median(rowYs.slice(1).map((y, i) => rowYs[i] - y))

  const absorbed = new Set()
  if (pitch > 0) {
    for (const y of ys) {
      if (ROW_START.test(text.get(y)) || HAS_FIGURE.test(text.get(y))) continue
      let best = null
      for (const ry of rowYs) if (best === null || Math.abs(ry - y) < Math.abs(best - y)) best = ry
      if (best === null || Math.abs(best - y) > pitch * 0.45) continue
      buckets.get(best).push(...buckets.get(y))
      absorbed.add(y)
    }
    for (const ry of rowYs) text.set(ry, cellsToText(buckets.get(ry)))
  }

  return ys.filter((y) => !absorbed.has(y)).map((y) => text.get(y)).filter(Boolean)
}

/* ── pdf.js extraction ─────────────────────────────────────────────────────── */

/** Turn a PDF File/Blob into text lines. pdf.js owns nothing but the geometry. */
export async function extractLines(file) {
  const pdfjs = await import('pdfjs-dist')
  // Vite resolves this to a hashed asset URL and serves it from our own origin.
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const buf = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: buf }).promise
  const lines = []

  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p)
    const content = await page.getTextContent()
    lines.push(...linesFromItems(content.items.map((i) => ({
      str: i.str, x: i.transform[4], y: i.transform[5], w: i.width,
    }))))
    page.cleanup?.()
  }

  doc.destroy?.()
  return lines
}

/** Convenience: File → parsed report. */
export async function parseSsoReportFile(file) {
  const lines = await extractLines(file)
  return parseSsoReport(lines)
}
